import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatPhone } from '../utils/formatters';
import SearchableSelect from './SearchableSelect';
import { useToast } from '../context/ToastContext';

interface SchoolClass {
  id: number;
  name: string;
}

export interface Parent {
  id?: number;
  name: string;
  email: string;
  phone: string;
  parent_type: 'Conecta' | 'Rendimento' | 'General';
  school_class_ids?: number[];
  school_classes?: { id: number; name: string }[];
  children_count?: number;
  is_active?: boolean;
}

interface ParentFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  parentToEdit?: Parent | null;
}

const ParentForm: React.FC<ParentFormProps> = ({ onSuccess, onCancel, parentToEdit }) => {
  const { addToast } = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [parentType, setParentType] = useState<'Conecta' | 'Rendimento' | 'General'>('General');
  const [childrenCount, setChildrenCount] = useState(1);
  const [isActive, setIsActive] = useState(true);
  const [selectedClasses, setSelectedClasses] = useState<number[]>([]);
  const [allClasses, setAllClasses] = useState<SchoolClass[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await api.get('classes/');
        setAllClasses(response.data);
      } catch (error) {
        console.error('Failed to fetch classes', error);
      }
    };
    fetchClasses();
  }, []);

  useEffect(() => {
    if (parentToEdit) {
      setName(parentToEdit.name);
      setEmail(parentToEdit.email);
      setPhone(formatPhone(parentToEdit.phone));
      setParentType(parentToEdit.parent_type || 'General');
      setChildrenCount(parentToEdit.children_count || 1);
      setIsActive(parentToEdit.is_active !== undefined ? parentToEdit.is_active : true);
      
      let classIds: number[] = [];
      if (parentToEdit.school_classes) {
        classIds = parentToEdit.school_classes.map(c => c.id);
      } else if (parentToEdit.school_class_ids) {
        classIds = parentToEdit.school_class_ids;
      }
      setSelectedClasses(classIds);
    } else {
      setName('');
      setEmail('');
      setPhone('');
      setParentType('General');
      setChildrenCount(1);
      setIsActive(true);
      setSelectedClasses([]);
    }
  }, [parentToEdit]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const parentData = { 
      name, 
      email, 
      phone, 
      parent_type: parentType,
      children_count: childrenCount,
      is_active: isActive,
      school_class_ids: selectedClasses 
    };

    try {
      if (parentToEdit && parentToEdit.id) {
        await api.patch(`parents/${parentToEdit.id}/`, parentData);
        addToast('Responsável atualizado com sucesso!', 'success');
      } else {
        await api.post('parents/', parentData);
        addToast('Novo responsável cadastrado!', 'success');
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save parent', error);
      addToast('Erro ao salvar os dados do responsável.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ title, icon }: { title: string, icon: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', marginTop: '10px' }}>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <h4 style={{ margin: 0, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-blue)' }}>{title}</h4>
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '25px', borderBottom: '1px solid var(--border-color)', paddingBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{parentToEdit ? '📝 Editar Cadastro' : '✨ Novo Cadastro de Responsável'}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Seção 1: Identificação */}
        <SectionTitle title="Dados Pessoais" icon="👤" />
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>Nome Completo</label>
            <input 
              type="text" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              placeholder="Digite o nome completo"
              required 
            />
          </div>
          <div className="form-group">
            <label>Telefone / WhatsApp</label>
            <input 
              type="text" 
              value={phone} 
              onChange={handlePhoneChange} 
              placeholder="(XX) XXXXX-XXXX"
              maxLength={15}
              required 
            />
          </div>
        </div>

        <div className="form-group">
          <label>E-mail de Contato</label>
          <input 
            type="email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            placeholder="email@exemplo.com"
            required 
          />
        </div>

        {/* Seção 2: Perfil Escolar */}
        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Vínculo Escolar" icon="🏫" />
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignItems: 'start' }}>
          <div className="form-group">
            <label>Tipo de Responsável</label>
            <select 
              value={parentType} 
              onChange={(e) => setParentType(e.target.value as any)} 
              required
              style={{ fontWeight: '500', color: parentType !== 'General' ? 'var(--secondary-orange)' : 'inherit' }}
            >
              <option value="General">Geral (Padrão)</option>
              <option value="Conecta">Pai Conecta</option>
              <option value="Rendimento">Rendimento</option>
            </select>
            <small style={{ color: 'var(--text-light)', marginTop: '4px', display: 'block' }}>
              Define a categoria de atuação no projeto.
            </small>
          </div>
          
          <div className="form-group">
            <label>Quantidade de Filhos</label>
            <input 
              type="number" 
              value={childrenCount} 
              onChange={(e) => setChildrenCount(parseInt(e.target.value))} 
              min="1" 
              required 
            />
          </div>
        </div>

        <div style={{ marginTop: '10px' }}>
          <SearchableSelect 
            label="Turmas Representadas"
            options={allClasses}
            value={selectedClasses}
            onChange={setSelectedClasses}
            multiple
            placeholder="Selecione uma ou mais turmas..."
          />
        </div>

        {/* Status e Ações */}
        <div style={{ 
          marginTop: '40px', 
          paddingTop: '20px', 
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
              <input 
                type="checkbox" 
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                style={{ width: '20px', height: '20px', marginRight: '10px' }}
              />
              <span style={{ fontWeight: '600', color: isActive ? 'var(--success)' : 'var(--text-light)' }}>
                {isActive ? 'Cadastro Ativo' : 'Cadastro Inativo'}
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onCancel} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ParentForm;
